import axios from 'axios';
import { getAuthenticationHeaders, isAuthenticationHeadersPresent, clearUserCredentialFromCache } from './utils';
import { API_BASE_URL } from '../globals';
import { getDeploymentEnvironment } from '../utils/utils';
import { isEmbeddedWebview } from '../utils/embeddedWebview';
import { getBridge } from '../utils/nativeBridge';

const IP_BLOCK_ERROR_CODE = 'IP_NOT_ALLOWED';
const IP_RESTRICTED_PATH = '/p/ip-restricted';

interface ApiError {
  response?: { status?: number; data?: { error?: { code?: string } } };
}

const isIpBlocked = (error: ApiError) =>
  error.response?.status === 403 &&
  error.response?.data?.error?.code === IP_BLOCK_ERROR_CODE;

const handleIpBlocked = () => {
  if (window.location.pathname !== IP_RESTRICTED_PATH) {
    window.location.replace(IP_RESTRICTED_PATH);
  }
};

const apiProvider = axios.create({});

apiProvider.interceptors.request.use((config) => {
  const urlBeingRequested = config.url;
  const regex = /\.shipsy\.(in|io)\/.*$/;
  const isMatch = regex.test(urlBeingRequested);
  const deploymentEnvironment = getDeploymentEnvironment();
  if (isMatch) {
    config.headers['x-forwarded-for-green'] = (deploymentEnvironment === 'green');
  }
  return config;
});



const handleAuthError = async (error: any) => {
  const status = error.response?.status;

  if (isEmbeddedWebview() && getBridge()?.api) {
    const url = error.config?.url ?? '';
    await getBridge()!.api.onStatus(status, url);
    return;
  }

  const apiErrorMessage = error.response?.data?.error?.message;
  const defaultErrorMessage = 'User logged out';
  const finalErrorMessage = apiErrorMessage || defaultErrorMessage;

  if (isAuthenticationHeadersPresent()) {
    clearUserCredentialFromCache();
    window.localStorage.setItem('login_page_error_message', finalErrorMessage);
  }

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Add 401 interceptor
apiProvider.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  if (isIpBlocked(error)) {
    handleIpBlocked();
    return new Promise(() => {});
  }
  if (error.response.status === 401) {
    await handleAuthError(error);
  }
  return Promise.reject(error);
});

const apiProviderWithBaseUrl = axios.create({baseURL: API_BASE_URL});

apiProviderWithBaseUrl.interceptors.request.use(async (config) => {
  const {headers} = config;
  const newHeaders = await getAuthenticationHeaders();
  if (headers) {
    config.headers = {...headers, ...newHeaders};
  }
  config.headers = newHeaders;
  const urlBeingRequested = config.url;
  const regex = /\.shipsy\.(in|io)\/.*$/;
  const isMatch = regex.test(urlBeingRequested);
  const deploymentEnvironment = getDeploymentEnvironment();
  if (isMatch) {
    config.headers['x-forwarded-for-green'] = (deploymentEnvironment === 'green');
  }
  return config;
});

// Add 401 interceptor
apiProviderWithBaseUrl.interceptors.response.use((response) => {
  return response;
}, async (error) => {
  if (isIpBlocked(error)) {
    handleIpBlocked();
    return new Promise(() => {});
  }
  if (error.response.status === 401) {
    await handleAuthError(error);
  }
  return Promise.reject(error);
});

export  {apiProviderWithBaseUrl};

export default apiProvider;
